import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { t } from 'i18next';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSInput from '@/components/POS/Common/POSInput';
import POSButton from '@/components/POS/Common/POSButton';
import { formatCurrency, formatPrice } from '@/scenes/POS/Core/pos.utils';
import { GetApiDirectWebstorePayments200DataItem } from '@/shared/api/models';
import { onlinePaymentsApi } from '../../Core/online-payments.api';
import { toast } from 'react-toastify';

interface RefundPaymentModalProps {
    open: boolean;
    onClose: () => void;
    payment: GetApiDirectWebstorePayments200DataItem | null;
    onSuccess: () => void;
}

export default function RefundPaymentModal({ open, onClose, payment, onSuccess }: RefundPaymentModalProps) {
    const [amount, setAmount] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const maxRefundAmount = useMemo(() => {
        if (!payment) {
            return 0;
        }
        const refunded = payment.refund_amount ?? 0;
        return Math.max(payment.amount - refunded, 0);
    }, [payment]);

    useEffect(() => {
        if (open) {
            setAmount('');
            setError('');
        }
    }, [open, payment?.transactionId]);

    const validateAmount = (value: string): string => {
        if (!value.trim()) {
            return t('POS.RefundAmountRequired');
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue) || numericValue < 1) {
            return t('POS.RefundAmountMin', { amount: formatCurrency(1) });
        }

        if (numericValue > maxRefundAmount) {
            return t('POS.RefundAmountMax', { amount: formatCurrency(maxRefundAmount) });
        }

        return '';
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = formatPrice(event.target.value);
        setAmount(value);
        setError(validateAmount(value));
    };

    const handleConfirm = async () => {
        if (!payment) {
            return;
        }

        const validationError = validateAmount(amount);
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setIsSubmitting(true);
            await onlinePaymentsApi.refundPayment(payment.transactionId, {
                amount: Number(amount),
            });
            toast.success(t('POS.RefundSuccess'));
            onSuccess();
            onClose();
        } catch {
            toast.error(t('POS.RefundFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper
                sx={{
                    position: 'relative',
                    maxWidth: '90%',
                    width: 480,
                    borderRadius: 8,
                    p: 4,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <POSHeading text={t('POS.RefundPaymentTitle')} />
                    {!isSubmitting && (
                        <IconButton onClick={onClose} aria-label="close">
                            <Close />
                        </IconButton>
                    )}
                </Box>

                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {t('POS.RefundAmountMax', { amount: formatCurrency(maxRefundAmount) })}
                    </Typography>

                    <POSInput
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder={t('POS.RefundAmount')}
                        type="text"
                        error={!!error}
                        helperText={error}
                        sx={{ width: '100%' }}
                    />

                    <Stack direction="row" justifyContent="flex-end" spacing={2}>
                        <POSButton title={t('Common.Dismiss')} variant="f_outline" onClick={() => onClose()} />
                        <POSButton
                            title={
                                isSubmitting ? (
                                    <Stack
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <CircularProgress size={16} color="inherit" />
                                        {t('POS.Processing')}
                                    </Stack>
                                ) : (
                                    t('Common.Confirm')
                                )
                            }
                            variant="save"
                            onClick={handleConfirm}
                            loading={isSubmitting}
                            disabled={isSubmitting || !!error || !amount}
                        />
                    </Stack>
                </Stack>
            </Paper>
        </Modal>
    );
}
